import { useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { CheckCircleIcon } from "vega-icons";

import { useGetRolesNew } from "@vega-api/generated/auth-permission-v1/auth-permission-v1";
import { RoleContext } from "@vega-api/generated/types/roleContext";
import { m } from "@vega-core/paraglide/messages";
import { buildTimeFlags } from "@vega-core/utils/buildTimeFlags";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { convertToTitleCase } from "@vega-core/utils/formatString";
import { isTruthy } from "@vega-core/utils/isTruthy";

type PermissionRow = {
  permission: string;
  roleValues: Record<string, boolean>;
};

type RoleColumn = { id: string; name: string };

const tableCellClasses =
  "base-body-medium text-text/primary bg-background/primary border-border/primary px-3 py-2";

const renderPermissionIcon = (value: boolean) =>
  value ? <CheckCircleIcon className="text-text/interactive" /> : null;

const createRolePermissionColumn = (
  roleId: string,
  header: string,
): ColumnDef<PermissionRow> => ({
  accessorFn: (row) => row.roleValues[roleId],
  id: roleId,
  header,
  cell: ({ getValue }) => (
    <div className="flex items-center justify-center">
      {renderPermissionIcon(isTruthy(getValue()))}
    </div>
  ),
  className: tableCellClasses,
});

const byPermissionCountDesc =
  (permissionsByRole: Record<string, Set<string>>) =>
  (a: RoleColumn, b: RoleColumn) =>
    permissionsByRole[b.id].size - permissionsByRole[a.id].size;

export const usePermissionsTableColumns = () => {
  const {
    data: rolesResponse,
    isLoading,
    isError,
  } = useGetRolesNew(
    { context: RoleContext.INVESTOR_PORTAL },
    { query: { select: (r) => r.data ?? [] } },
  );

  const roles = rolesResponse ?? [];

  const { permissionRows, groupedHeaders, columns } = useMemo(() => {
    const permissionsByRole = Object.fromEntries(
      roles.map((role) => [
        role.id,
        new Set(role.permissions.map((p) => p.featureName)),
      ]),
    ) as Record<string, Set<string>>;

    const toRoleColumn = (r: { id: string; name: string }): RoleColumn => ({
      id: r.id,
      name: convertToTitleCase(r.name),
    });

    const offeringRoles = roles
      .filter((r) => r.contexts.includes(RoleContext.PRE_INVESTMENT))
      .map(toRoleColumn)
      .sort(byPermissionCountDesc(permissionsByRole));

    const investmentRoles = buildTimeFlags.reporting_settings_investments
      ? roles
          .filter((r) => r.contexts.includes(RoleContext.POST_INVESTMENT))
          .map(toRoleColumn)
          .sort(byPermissionCountDesc(permissionsByRole))
      : [];

    const allRoles = [...offeringRoles, ...investmentRoles];

    const descriptionByFeatureName = Object.fromEntries(
      roles.flatMap((role) =>
        role.permissions.map((p) => [p.featureName, p.description]),
      ),
    );

    const uniquePermissions = Array.from(
      new Set(
        roles.flatMap((role) => role.permissions.map((p) => p.featureName)),
      ),
    );

    const rows: PermissionRow[] = uniquePermissions.map((featureName) => ({
      permission: descriptionByFeatureName[featureName],
      roleValues: Object.fromEntries(
        allRoles.map((role) => [
          role.id,
          permissionsByRole[role.id].has(featureName),
        ]),
      ),
    }));

    const headers: { label: string; colSpan: number }[] = [];

    if (offeringRoles.length > 0) {
      headers.push({
        label: m.userManagement_permissions_offerings(),
        colSpan: offeringRoles.length,
      });
    }

    if (investmentRoles.length > 0) {
      headers.push({
        label: m.userManagement_permissions_investments(),
        colSpan: investmentRoles.length,
      });
    }

    const tableColumns: ColumnDef<PermissionRow>[] = [
      {
        accessorKey: "permission",
        header: m.userManagement_permissions_permission(),
        cell: ({ getValue }) => getValue<string>(),
        className: cnTwMerge(tableCellClasses, "border-r text-left"),
      },
      ...allRoles.map((role) => createRolePermissionColumn(role.id, role.name)),
    ];

    return {
      permissionRows: rows,
      groupedHeaders: headers,
      columns: tableColumns,
    };
  }, [roles]);

  return { permissionRows, groupedHeaders, columns, isLoading, isError };
};
